/**
 * Which company an owner is looking at, and whether they have said.
 *
 * Everyone except an owner belongs to exactly one company and never sees any
 * of this. An owner belongs to none, so every company-scoped request needs to
 * be told which one — and until now "not told" meant "all of them".
 *
 * That default is how a screen could quietly mix tenants: the Daily Calls
 * Report counted 35,032 candidates to call back across every company at once,
 * and 33 list routes answered the same way. Nobody chose it; it was what
 * happened when nobody chose anything.
 *
 * So there are now three states, not two:
 *
 * - **null** — nothing chosen. Company-scoped screens show a prompt rather
 *   than data. This is where an owner starts every session.
 * - **ALL_COMPANIES** — every company, chosen deliberately. The audit log,
 *   the companies list and receivables are read this way on purpose.
 * - **a company id** — one company, and the only state in which records can
 *   be created.
 *
 * The middle one is the point. Reading across tenants was never wrong; doing
 * it without having asked for it was.
 */

/**
 * Stored in place of a company id when the owner picks "All companies".
 *
 * A sentinel rather than a second field because it travels through
 * ``actingCompanyId`` — localStorage, the request interceptor, the header
 * picker — and every one of those places already handles a nullable string.
 * Two fields would mean four combinations, two of which are nonsense.
 *
 * Not a valid id: ids here are prefixed and hyphen-free (``cmp_a777d27c5da1``),
 * so this cannot collide with one.
 */
export const ALL_COMPANIES = '__all__';

/** Has the owner chosen anything at all? */
export function hasCompanyChoice(acting: string | null | undefined): boolean {
  return Boolean(acting);
}

/** Is every company deliberately in view? */
export function isAllCompanies(acting: string | null | undefined): boolean {
  return acting === ALL_COMPANIES;
}

/**
 * Is exactly one company in view?
 *
 * The question anything that *writes* has to ask. "All companies" is a
 * perfectly good answer for reading and no answer at all for creating a
 * record, which is why the server refuses those with 400 rather than picking
 * a company itself.
 */
export function hasOneCompany(acting: string | null | undefined): boolean {
  return Boolean(acting) && acting !== ALL_COMPANIES;
}

/**
 * The value to send as ``company_id``, or null to send none.
 *
 * Null for both "nothing chosen" and "all companies", because the API has one
 * way of saying every tenant and it is the absence of the parameter. The
 * difference between the two lives in this application, not on the wire.
 */
export function requestCompanyId(acting: string | null | undefined): string | null {
  return !acting || acting === ALL_COMPANIES ? null : acting;
}
